import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import { usePromoStatus } from "../hooks/usePromoStatus";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDateShort } from "../lib/currency";

function DaysBadge({ days, expired }) {
  const { t } = useTranslation();
  let cls = "bg-emerald-50 text-emerald-700 ring-emerald-100";
  if (expired) cls = "bg-slate-100 text-slate-600 ring-slate-200";
  else if (days <= 3) cls = "bg-rose-50 text-rose-700 ring-rose-100";
  else if (days <= 7) cls = "bg-amber-50 text-amber-700 ring-amber-100";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${cls}`} data-testid="trial-days-badge">
      {expired ? t("admin_trials.expired") : t("admin_trials.days_left", { count: days })}
    </span>
  );
}

export default function AdminTrials() {
  const { t } = useTranslation();
  const promo = usePromoStatus();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeExpired, setIncludeExpired] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/trials", { params: { include_expired: includeExpired } });
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setLoading(false); }
  }, [includeExpired, t]);

  useEffect(() => { load(); }, [load]);

  const filtered = q ? rows.filter((r) => r.org_name.toLowerCase().includes(q.toLowerCase())) : rows;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-trials-page">
      <Header />
      <Toaster richColors position="top-right" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("admin_trials.eyebrow")}</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900" data-testid="admin-trials-title">
            {t("admin_trials.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">{t("admin_trials.subtitle")}</p>
        </div>

        {promo && promo.active && (
          <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="admin-trials-promo-banner">
            <span className="mr-1">🌞</span>
            <strong>{t("promo.summer_label")}</strong> — {t("admin_trials.promo_banner", { date: formatDateShort(promo.end_date), days: promo.days_remaining || 0 })}
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            placeholder={t("admin_trials.search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            data-testid="admin-trials-search"
          />
          <label className="ml-2 inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={includeExpired}
              onChange={(e) => setIncludeExpired(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600"
              data-testid="admin-trials-include-expired"
            />
            {t("admin_trials.include_expired")}
          </label>
          <span className="ml-auto text-xs text-slate-500">
            {t("admin_trials.total_count", { count: filtered.length })}
          </span>
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          {loading ? (
            <p className="text-sm text-slate-500 p-2">{t("common.loading")}</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-500 p-2" data-testid="admin-trials-empty">{t("admin_trials.empty")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 text-left">{t("admin_trials.col_agency")}</th>
                  <th className="py-2 text-left">{t("admin_trials.col_contact")}</th>
                  <th className="py-2 text-left">{t("admin_trials.col_started")}</th>
                  <th className="py-2 text-left">{t("admin_trials.col_ends")}</th>
                  <th className="py-2 text-left">{t("admin_trials.col_days")}</th>
                  <th className="py-2 text-right">{t("admin_trials.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.org_id} className="border-t border-slate-100" data-testid={`trial-row-${r.org_id}`}>
                    <td className="py-2 text-slate-900 font-medium">{r.org_name}</td>
                    <td className="py-2 text-slate-700">
                      <div>{r.contact_name || "—"}</div>
                      <div className="text-xs text-slate-500">{r.contact_email || "—"}</div>
                    </td>
                    <td className="py-2 text-slate-600">{formatDateShort(r.trial_started_at)}</td>
                    <td className="py-2 text-slate-600">{formatDateShort(r.trial_ends_at)}</td>
                    <td className="py-2"><DaysBadge days={r.days_remaining} expired={r.expired} /></td>
                    <td className="py-2 text-right">
                      <Link
                        to={`/agencies/${r.org_id}`}
                        className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        data-testid={`trial-view-${r.org_id}`}
                      >
                        {t("admin_trials.view_agency")}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  );
}
